/**
 * jw-components.js
 * Quick Add Drawer — Split Mobile/PC
 * v2.1 — 2026-07-08
 * Changes: refreshSkuRow, bindTierCardClicks, oninput real-time qty, strikethrough align
 *
 * Mobile: bottom drawer, slides up via translate
 * PC: centered modal, display toggle
 * All DOM selection uses data-* attributes only.
 */

// ─── Image Loading Shields ───
const TRANSPARENT_HOLDER = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1' height='1'></svg>";
const IMG_FADE_STYLE = "opacity:0;transition:opacity 0.25s ease-in-out;";
const IMG_ONLOAD_STATE = `if(this.dataset.src){this.onload=function(){this.style.opacity=1};this.src=this.dataset.src;delete this.dataset.src;}`;
const IMG_ONERROR_FALLBACK = "this.style.background='#F3F4F6'; this.style.padding='12px';";

document.addEventListener('DOMContentLoaded', function () {
  initJWComponents();
});

// Expose functions to window for external callers (collection.js, jw-product-mobile.js, etc.)
window.openQuickAddDrawer = openQuickAddDrawer;
window.closeQuickAddDrawer = closeQuickAddDrawer;
window.showAddToCartModal = showDrawerUI;       // backward compat — collection.js calls this
window.fetchProductVariants = fetchProductVariants;

// =====================
// State
// =====================

let currentSkuData = [];
let currentPriceTiers = [];
let currentActiveSkuId = null;
let currentProductImages = [];
let currentImageIndex = 0;
let savedScrollY = 0;
let currentImageBinding = null;   // Phase F1.2: imageBinding from preRenderedData
var _pricingReady = false;        // F2.6.3.1: true when JWProductPricing is initialized

// Image viewer state (self-contained, no module deps)
let viewerImages = [];
let viewerIndex = 0;

function openDrawerImageViewer(images, startIndex) {
  var viewer = document.getElementById('viewer');
  if (!viewer || !images || images.length === 0) return;
  viewerImages = images;
  viewerIndex = startIndex || 0;

  var vImg = document.getElementById('vImg');
  var vPage = document.getElementById('vPage');
  if (vImg) vImg.src = images[viewerIndex];
  if (vPage) vPage.textContent = (viewerIndex + 1) + '/' + images.length;

  renderViewerThumbs();
  viewer.classList.add('on');
  document.body.style.overflow = 'hidden';
}

function renderViewerThumbs() {
  var pcContainer = document.getElementById('vThumbsPC');
  var mobContainer = document.getElementById('vThumbsMob');
  if (pcContainer) pcContainer.innerHTML = '';
  if (mobContainer) mobContainer.innerHTML = '';

  viewerImages.forEach(function(src, i) {
    var d = document.createElement('div');
    d.className = 'w-[80px] h-[80px] shrink-0 rounded-md overflow-hidden border-2 cursor-pointer transition ' + (i === viewerIndex ? 'border-white' : 'border-transparent hover:border-white/50');
    d.innerHTML = '<img src="' + src + '" class="w-full h-full object-cover" loading="lazy">';
    d.onclick = function() {
      viewerIndex = i;
      var vImg = document.getElementById('vImg');
      if (vImg) vImg.src = src;
      var vPage = document.getElementById('vPage');
      if (vPage) vPage.textContent = (i + 1) + '/' + viewerImages.length;
      renderViewerThumbs();
    };
    if (pcContainer) pcContainer.appendChild(d);

    if (mobContainer) {
      var dm = d.cloneNode(true);
      dm.className = 'w-[60px] h-[60px] shrink-0 rounded-md overflow-hidden border-2 cursor-pointer transition ' + (i === viewerIndex ? 'border-white' : 'border-transparent hover:border-white/50');
      var imgEl = dm.querySelector('img');
      imgEl.onclick = d.querySelector('img').onclick;
      mobContainer.appendChild(dm);
    }
  });
}

function closeDrawerImageViewer() {
  var viewer = document.getElementById('viewer');
  if (!viewer) return;
  viewer.classList.remove('on');
  document.body.style.overflow = '';
}

function initViewerEvents() {
  var closeBtn = document.getElementById('vClose');
  var prevBtn = document.getElementById('vPrev');
  var nextBtn = document.getElementById('vNext');
  var viewer = document.getElementById('viewer');

  // Close button
  if (closeBtn && !closeBtn._viewerBound) {
    closeBtn._viewerBound = true;
    closeBtn.onclick = closeDrawerImageViewer;
  }

  // Click backdrop to close
  if (viewer && !viewer._viewerBound) {
    viewer._viewerBound = true;
    viewer.onclick = function(e) {
      if (e.target === viewer) closeDrawerImageViewer();
    };
  }

  // Prev
  if (prevBtn && !prevBtn._viewerBound) {
    prevBtn._viewerBound = true;
    prevBtn.onclick = function() {
      var len = viewerImages.length;
      viewerIndex = (viewerIndex - 1 + len) % len;
      var vImg = document.getElementById('vImg');
      if (vImg) vImg.src = viewerImages[viewerIndex];
      var vPage = document.getElementById('vPage');
      if (vPage) vPage.textContent = (viewerIndex + 1) + '/' + len;
      renderViewerThumbs();
    };
  }

  // Next
  if (nextBtn && !nextBtn._viewerBound) {
    nextBtn._viewerBound = true;
    nextBtn.onclick = function() {
      var len = viewerImages.length;
      viewerIndex = (viewerIndex + 1) % len;
      var vImg = document.getElementById('vImg');
      if (vImg) vImg.src = viewerImages[viewerIndex];
      var vPage = document.getElementById('vPage');
      if (vPage) vPage.textContent = (viewerIndex + 1) + '/' + len;
      renderViewerThumbs();
    };
  }

  // Keyboard nav
  if (!window._viewerKeyBound) {
    window._viewerKeyBound = true;
    document.addEventListener('keydown', function(e) {
      if (!viewer || !viewer.classList.contains('on')) return;
      if (e.key === 'Escape') closeDrawerImageViewer();
      if (e.key === 'ArrowLeft' && prevBtn) prevBtn.click();
      if (e.key === 'ArrowRight' && nextBtn) nextBtn.click();
    });
  }
}

// Init viewer events on script load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initViewerEvents);
} else {
  initViewerEvents();
}

// Swiper instances
let pcDrawerSwiper = null;
let pcThumbSwiper = null;

// Swiper lazy-load state
let swiperLoaded = false;
let swiperLoadPromise = null;

// =====================
// Swiper Lazy Loader
// =====================

function loadSwiper() {
  if (swiperLoaded && window.Swiper) return Promise.resolve();
  if (swiperLoadPromise) return swiperLoadPromise;

  swiperLoadPromise = new Promise((resolve, reject) => {
    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
    document.head.appendChild(link);

    // Load JS
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
    script.onload = function () {
      swiperLoaded = true;
      resolve();
    };
    script.onerror = function () {
      swiperLoadPromise = null;
      reject(new Error('Failed to load Swiper'));
    };
    document.head.appendChild(script);
  });

  return swiperLoadPromise;
}

// =====================
// Price Contract v1.1 — Marketing Engine
// ALL "X% OFF" badges come from variant.compare_at_price ONLY.
// Tier Engine (tier_discount_rate) is audit-only, never rendered.
// =====================

// v1.1: Marketing Engine visual discount — Full Resolver preferred, Lite path when unavailable
function getVisualDiscountBySku(skuId) {
  var facts = resolvePricingFacts(skuId, 1);
  return facts ? facts.marketingDiscountPercent : 0;
}

// =====================
// Refresh Single SKU Row (price / strikethrough / discount badge)
// Aligned with PC product page: tierPrice < basePrice → show strikethrough
// =====================

function refreshSkuRow(variantId) {
  var sku = currentSkuData.find(function(s) { return s.id === variantId; });
  if (!sku) return;

  var qty = sku.qty > 0 ? sku.qty : 1;
  var facts = resolvePricingFacts(variantId, qty);
  if (!facts) return;

  var priceStr = '$' + facts.resolvedPrice.toFixed(2);
  var strikeStr = '$' + facts.compareAtDollars.toFixed(2);
  var discountPercent = facts.marketingDiscountPercent;
  var hasDiscount = facts.hasMarketingDiscount;

  var rows = document.querySelectorAll('[data-sku-item][data-variant-id="' + variantId + '"]');
  rows.forEach(function(row) {
    var priceEl = findPriceElInRow(row);
    if (priceEl) priceEl.textContent = priceStr;

    var strikeEl = row.querySelector('.line-through');
    if (strikeEl) {
      strikeEl.textContent = strikeStr;
      if (hasDiscount) strikeEl.classList.remove('hidden');
      else strikeEl.classList.add('hidden');
    }

    var badge = row.querySelector('.sku-badge');
    if (badge) {
      if (discountPercent > 0) {
        badge.textContent = '-' + discountPercent + '%';
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    }
  });
}

function findPriceElInRow(row) {
  var boldEls = row.querySelectorAll('.text-sm.font-bold');
  for (var i = 0; i < boldEls.length; i++) {
    if (boldEls[i].textContent.trim().startsWith('$')) return boldEls[i];
  }
  return null;
}

// =====================
// Init
// =====================

function initJWComponents() {
  setupAddToCartButtons();
  setupDrawerEvents();
}

// =====================
// Product Card Click → Open Drawer
// =====================

function setupAddToCartButtons() {
  // Quick Add Drawer is now triggered only by [data-quick-add] elements
  // handled by collection.js direct binding. No card-level click handler.
}

// =====================
// Drawer Open / Close
// =====================

function openQuickAddDrawer(handle, preRenderedData) {
  // Reset state
  currentSkuData = [];
  currentPriceTiers = [];
  currentActiveSkuId = null;
  currentProductImages = [];
  currentImageIndex = 0;
  currentImageBinding = (preRenderedData && preRenderedData.imageBinding) || null;  // Phase F1.2
  _pricingReady = false;  // Reset — re-initialized in fetchProductVariants if JWProductPricing loaded

  // Show the drawers (but mobile stays invisible until animation)
  showDrawerUI();

  // Fetch and render data
  fetchProductVariants(handle, preRenderedData || {});
}

function showDrawerUI() {
  const isMobile = window.innerWidth < 768;

  if (isMobile) {
    // --- Mobile only: invisible → visible, translate-y-full → translate-y-0 ---
    const mobileEl = document.querySelector('[data-quick-add-mobile]');
    if (mobileEl) {
      const panel = mobileEl.querySelector('[data-mobile-panel]');
      mobileEl.classList.remove('invisible', 'pointer-events-none');
      mobileEl.classList.add('opacity-0');
      if (panel) panel.style.translate = '0 100%';

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          mobileEl.classList.remove('opacity-0');
          mobileEl.classList.add('opacity-100');
          if (panel) panel.style.translate = '0 0';
        });
      });
    }
  } else {
    // --- PC only: just unhide ---
    const pcEl = document.querySelector('[data-quick-add-pc]');
    if (pcEl) {
      pcEl.classList.remove('hidden');
      pcEl.classList.add('flex');
    }
  }

  lockBodyScroll();
}

function closeQuickAddDrawer() {
  const isMobile = window.innerWidth < 768;

  if (isMobile) {
    // --- Mobile only: animate out ---
    const mobileEl = document.querySelector('[data-quick-add-mobile]');
    if (mobileEl) {
      const panel = mobileEl.querySelector('[data-mobile-panel]');
      mobileEl.classList.remove('opacity-100');
      mobileEl.classList.add('opacity-0');
      if (panel) panel.style.translate = '0 100%';

      setTimeout(() => {
        mobileEl.classList.add('invisible', 'pointer-events-none');
        mobileEl.classList.remove('opacity-100');
        // 动画结束后再恢复 body 滚动，防止滚动条和抽屉消失不同步造成跳动
        unlockBodyScroll();
      }, 300);
    }
  } else {
    // --- PC only: hide ---
    const pcEl = document.querySelector('[data-quick-add-pc]');
    if (pcEl) {
      pcEl.classList.add('hidden');
      pcEl.classList.remove('flex');
    }
    // PC 无退出动画，立即恢复
    unlockBodyScroll();
  }
}

function lockBodyScroll() {
  savedScrollY = window.scrollY;
  // 补偿滚动条宽度，防止关闭抽屉时内容左右跳动
  var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = scrollbarWidth + 'px';
  }
  document.body.style.setProperty('--scroll-y', savedScrollY + 'px');
  document.body.classList.add('drawer-open');
}

function unlockBodyScroll() {
  document.body.classList.remove('drawer-open');
  document.body.style.removeProperty('--scroll-y');
  document.body.style.paddingRight = '';
  // behavior: 'instant' 防止浏览器默认 smooth scroll 造成短暂跳动
  window.scrollTo({ top: savedScrollY, behavior: 'instant' });
}

// =====================
// Drawer Event Bindings
// =====================

function setupDrawerEvents() {
  // Mobile close button
  const mobileClose = document.querySelector('[data-mobile-close]');
  if (mobileClose) mobileClose.addEventListener('click', closeQuickAddDrawer);

  // Mobile backdrop
  const mobileBackdrop = document.querySelector('[data-mobile-backdrop]');
  if (mobileBackdrop) mobileBackdrop.addEventListener('click', closeQuickAddDrawer);

  // PC close button
  const pcClose = document.querySelector('[data-pc-close]');
  if (pcClose) pcClose.addEventListener('click', closeQuickAddDrawer);

  // PC backdrop
  const pcBackdrop = document.querySelector('[data-pc-backdrop]');
  if (pcBackdrop) pcBackdrop.addEventListener('click', closeQuickAddDrawer);

  // PC image nav — Swiper-based
  const pcImgPrev = document.querySelector('[data-pc-img-prev]');
  const pcImgNext = document.querySelector('[data-pc-img-next]');
  if (pcImgPrev) pcImgPrev.addEventListener('click', () => { if (pcDrawerSwiper) pcDrawerSwiper.slidePrev(); });
  if (pcImgNext) pcImgNext.addEventListener('click', () => { if (pcDrawerSwiper) pcDrawerSwiper.slideNext(); });

  // PC thumbnail nav — Swiper-based
  const pcThumbPrev = document.querySelector('[data-pc-thumb-prev]');
  const pcThumbNext = document.querySelector('[data-pc-thumb-next]');
  if (pcThumbPrev) pcThumbPrev.addEventListener('click', () => { if (pcThumbSwiper) pcThumbSwiper.slidePrev(); });
  if (pcThumbNext) pcThumbNext.addEventListener('click', () => { if (pcThumbSwiper) pcThumbSwiper.slideNext(); });

  // Mobile add-to-cart
  const mobileAddBtn = document.querySelector('[data-mobile-add-btn]');
  if (mobileAddBtn) mobileAddBtn.addEventListener('click', handleAddToCart);

  // PC add-to-cart
  const pcAddBtn = document.querySelector('[data-pc-add-btn]');
  if (pcAddBtn) pcAddBtn.addEventListener('click', handleAddToCart);

  // Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeQuickAddDrawer();
  });
}

// ═══════════════ Lite Pricing Resolver ═══════════════
// Rule 5 — Runtime Availability Independence
// Peer to JWProductPricing (Full Resolver), NOT a fallback.
// Operates on Compiler Facts (sku.priceMap) when Full Domain
// Resolver is not available (Collection / Search / QuickView).
//
// Constraints:
// - MUST consume only Compiler Facts (tiers, price_base, compare_at_price)
// - MUST NOT recalculate Commerce Facts
// - tierPresentationSavingPercent → Tier Card ONLY (never badge/pill)
// - deriveMarketingDiscount → Presentation Derivation, not Compiler output
// - resolvedPrice → UI display only, NOT Cart authority

var JWLitePricingResolver = (function() {
  'use strict';

  function _selectTier(tiers, baseTierPrice, qty) {
    if (!tiers || tiers.length === 0) return baseTierPrice;
    for (var i = tiers.length - 1; i >= 0; i--) {
      if (qty >= Number(tiers[i].min)) {
        var p = Number(tiers[i].price);
        return (isFinite(p) && p > 0) ? p : baseTierPrice;
      }
    }
    var fp = Number(tiers[0].price);
    return (isFinite(fp) && fp > 0) ? fp : baseTierPrice;
  }

  function deriveMarketingDiscount(priceMap) {
    if (!priceMap) return { marketingDiscountPercent: 0, compareAtDollars: 0, hasMarketingDiscount: false };
    var price     = Number(priceMap.price_base);
    var compareAt = Number(priceMap.compare_at_price) || 0;
    if (compareAt > 0 && price > 0 && compareAt > price) {
      var pct = Math.round((1 - price / compareAt) * 100);
      return { marketingDiscountPercent: pct, compareAtDollars: compareAt, hasMarketingDiscount: pct > 0 };
    }
    return { marketingDiscountPercent: 0, compareAtDollars: compareAt, hasMarketingDiscount: false };
  }

  function getActiveTierIndex(tiers, qty) {
    if (!tiers || tiers.length === 0) return -1;
    var idx = 0;
    for (var i = 0; i < tiers.length; i++) {
      if (qty >= Number(tiers[i].min)) idx = i;
    }
    return idx;
  }

  function resolveFacts(sku, qty) {
    if (!sku || !sku.priceMap) return null;
    var pm         = sku.priceMap;
    var tiers      = pm.tiers || [];
    var btp        = Number(pm.price_base) || 0;           // baseTierPrice
    var resPrice   = _selectTier(tiers, btp, qty);
    var marketing  = deriveMarketingDiscount(pm);

    return {
      schemaVersion:                  "1.1",
      tiers:                          tiers,
      baseTierPrice:                  btp,
      resolvedPrice:                  resPrice,
      marketingDiscountPercent:       marketing.marketingDiscountPercent,
      hasMarketingDiscount:           marketing.hasMarketingDiscount,
      compareAtDollars:               marketing.compareAtDollars,
      tierPresentationSavingPercent:  (btp > 0 && resPrice < btp)
                                      ? Math.round((1 - resPrice / btp) * 100)
                                      : 0,
      activeTierIndex:                getActiveTierIndex(tiers, qty)
    };
  }

  return {
    resolveFacts:           resolveFacts,
    deriveMarketingDiscount: deriveMarketingDiscount,
    getActiveTierIndex:     getActiveTierIndex,
    _selectTier:            _selectTier
  };
})();

// ═══════════════ Pricing Source Resolver ═══════════════
// v1.1 Frozen Shape:
// {
//   schemaVersion:           "1.1",
//   tiers,                   // Compiler tiers[]   — Tier Card
//   baseTierPrice,           // tier[0].price      — Tier Card anchor
//   resolvedPrice,           // tier-matched price  — UI display (NOT Cart authority)
//   marketingDiscountPercent,// Presentation        — badge / pill / OFF tag
//   hasMarketingDiscount,    // Presentation        — strikethrough visibility
//   compareAtDollars,        // Presentation        — strikethrough amount
//   tierPresentationSavingPercent, // Presentation  — Tier Card ONLY
//   activeTierIndex          // matched tier index  — highlight
// }

function resolvePricingFacts(skuId, qty) {
  var sku = currentSkuData.find(function(s) { return s.id === skuId; });
  if (!sku) return null;
  var q = qty > 0 ? qty : 1;

  if (_pricingReady) {
    var facts  = window.JWProductPricing.getResolvedFacts(skuId, q);
    var mktPct = window.JWProductPricing.getDiscountPct(skuId);
    var compAt = window.JWProductPricing.getCompareAtPrice(skuId);
    return {
      schemaVersion:                  "1.1",
      tiers:                          facts.tiers,
      baseTierPrice:                  Number(facts.basePrice.dollars),
      resolvedPrice:                  Number(facts.resolvedPrice.dollars),
      marketingDiscountPercent:       mktPct,
      hasMarketingDiscount:           mktPct > 0,
      compareAtDollars:               compAt / 100,
      tierPresentationSavingPercent:  facts.discountPercent,
      activeTierIndex:                window.JWProductPricing.getActiveTierIndex(skuId, q)
    };
  }

  return JWLitePricingResolver.resolveFacts(sku, q);
}

// =====================
// F2.6.3.1: Bridge QuickAdd priceMap → JWProductPricing tierData
// =====================
function _initPricingForQuickAdd() {
  if (!window.JWProductPricing) {
    return false;  // Collection pages: JWProductPricing not loaded — pricing features disabled
  }

  var tierData = {};
  var variantsById = {};
  var variantsList = [];

  currentSkuData.forEach(function(s) {
    var tiers = [];
    if (s.priceMap && Array.isArray(s.priceMap.tiers)) {
      tiers = s.priceMap.tiers.map(function(t) {
        return {
          min: t.min,
          price: typeof t.price === 'number' ? t.price : (t.price || t.discount || 0),
          tier_discount_rate: t.tier_discount_rate != null ? Number(t.tier_discount_rate) : (t.discount_rate != null ? Number(t.discount_rate) : 0)
        };
      });
    }
    tierData[s.id] = { tiers: tiers };

    // Build variant lookup (prices in cents, matching Shopify / JWProductContext format)
    var variant = {
      id: s.id,
      price: Math.round(s.priceBase * 100),
      compare_at_price: Math.round((s.compareAtPrice || 0) * 100)
    };
    variantsById[s.id] = variant;
    variantsList.push(variant);
  });

  window.JWProductPricing.init(
    { variants: { byId: variantsById, list: variantsList }, product: { moq: 1 } },
    {},
    tierData
  );
  _pricingReady = true;
  return true;
}

// =====================
// Fetch Product Variants
// =====================

async function fetchProductVariants(handle, preRenderedData = {}) {
  try {
    const response = await fetch(`/products/${handle}.js`);
    const product = await response.json();

    // Images
    let images = [];
    if (product.images && Array.isArray(product.images)) {
      images = product.images.map(img => typeof img === 'string' ? img : (img.src || '')).filter(Boolean);
    }

    const variants = product.variants || [];

    // Price tiers from preRenderedData or fallback
    let priceTiers = preRenderedData?.prices && Object.keys(preRenderedData.prices).length > 0
      ? preRenderedData.prices
      : {};

    let skuImages = preRenderedData?.skuImages && Object.keys(preRenderedData.skuImages).length > 0
      ? preRenderedData.skuImages
      : {};

    // Phase F1.2: Capture imageBinding for shared resolver
    if (preRenderedData?.imageBinding) {
      currentImageBinding = preRenderedData.imageBinding;
    }

    // Build SKU data
    // IMPORTANT: All actual prices come from metafields (priceMap).
    // Shopify's v.price / v.compare_at_price are ONLY used for strikethrough display.
    // Fallback: if no metafield prices, use variant price (Shopify .js API returns cents)
    currentSkuData = variants.map(v => {
      const variantPriceDollars = (typeof v.price === 'number' ? v.price / 100 : parseFloat(v.price) || 0);
      const tierData = priceTiers[v.sku] || { tiers: [{ min: 1, max: null, discount: variantPriceDollars, percent: 0 }] };
      // variant.price = sole runtime base price authority (Shopify cents → dollars)
      let priceBase = variantPriceDollars;

      const tiers = (tierData.tiers || []).map(t => ({
        min: typeof t.min === 'number' ? t.min : parseInt(t.min, 10),
        max: t.max === undefined ? undefined : (typeof t.max === 'number' ? t.max : parseInt(t.max, 10)),
        discount: typeof t.discount === 'number' ? t.discount : parseFloat(t.discount),
        price: typeof t.price === 'number' ? t.price : (typeof t.discount === 'number' ? t.discount : (parseFloat(t.discount) || priceBase)),
        discount_rate: t.tier_discount_rate != null ? Number(t.tier_discount_rate) : (t.discount_rate != null ? Number(t.discount_rate) : undefined)
      }));

      // Phase F1.2: Shared resolver for variant image (single source of truth)
      let variantImage = '';
      if (currentImageBinding && window.JWImageResolver) {
        var tmpSku = { options: [v.option1 || ''] };
        variantImage = window.JWImageResolver.resolve(tmpSku, currentImageBinding, skuImages);
      }
      // Fallback: original SKU image lookup when resolver returns empty
      if (!variantImage) {
        const imgObj = skuImages[v.sku];
        if (imgObj) {
          variantImage = typeof imgObj === 'string' ? imgObj : (imgObj.large || imgObj.thumb || '');
        } else if (v.featured_image) {
          variantImage = typeof v.featured_image === 'string' ? v.featured_image : (v.featured_image.src || '');
        } else if (images.length > 0) {
          variantImage = images[0];
        }
      }

      // Build variant display name from option values
      const optionValues = [v.option1, v.option2, v.option3].filter(Boolean);
      const variantName = optionValues.length > 0 ? optionValues.join(' / ') : (v.title || 'Default');

      // compare_at_price from Shopify API → only for strikethrough display
      const compareAtPrice = v.compare_at_price ? v.compare_at_price / 100 : 0;

      // Build options array for display (e.g. [{name:'Color',value:'Pink'}, ...])
      const optionNames = (product.options || []).map(o => o.name);
      const options = [v.option1, v.option2, v.option3].map((val, i) => ({
        name: optionNames[i] || '',
        value: val || ''
      })).filter(o => o.value);

      return {
        id: v.id,
        sku: v.sku || '',
        name: variantName,
        options: options,
        // variant.price (converted to dollars) = sole runtime base price authority
        priceBase: priceBase,
        // compare_at_price from Shopify API → Marketing Engine authority (Price Contract v1.1)
        compareAtPrice: compareAtPrice,
        image: variantImage,
        qty: 0,
        priceMap: { price_base: priceBase, compare_at_price: compareAtPrice, tiers }
      };
    });

    if (currentSkuData.length > 0) {
      currentPriceTiers = currentSkuData[0].priceMap.tiers || [];
      window.productData = window.productData || {};
      window.productData.priceTiers = currentPriceTiers;
    }

    // Determine tags — only Free Shipping here; discount tag is managed by updateHeaderDiscountTag
    const activeTags = [];
    if (preRenderedData?.freeShipping || preRenderedData?.free_shipping) activeTags.push('Free Shipping');

    // Variant option names (e.g. "Size / Item Style")
    const optionNames = (product.options || []).map(o => o.name).join(' / ');

    // --- Populate UI ---
    populateHeader(preRenderedData?.title || product.title, preRenderedData?.moq || '1', activeTags, optionNames);

    // Use SKU images as the default image set (each SKU has its own variant image)
    var skuImagesList = currentSkuData.map(function(s) { return s.image; }).filter(Boolean);
    await populateImages(skuImagesList.length > 0 ? skuImagesList : images);

    currentActiveSkuId = currentSkuData[0]?.id || null;

    // F2.6.3.1: Initialize JWProductPricing with QuickAdd data (bridge priceMap → tierData)
    _initPricingForQuickAdd();

    // Set initial discount tag for first SKU (works on both Full & Lite paths)
    if (currentSkuData[0]) {
      updateHeaderDiscountTag(currentSkuData[0]);
    }

    // Render SKU lists (both mobile & PC)
    renderSkuData(currentSkuData);
    setupQuantityButtons();

    // Render tier pricing for first SKU (works on both Full & Lite paths)
    if (currentActiveSkuId) {
      const firstSku = currentSkuData[0];
      const hasTiers = firstSku && firstSku.priceMap
        && Array.isArray(firstSku.priceMap.tiers)
        && firstSku.priceMap.tiers.length > 0;
      if (hasTiers) {
        renderTierPricing(currentActiveSkuId);
      } else {
        // First SKU has no tiers — explicitly hide BOTH Drawer containers
        // (must be scoped to Drawer; unscoped selectors would target product.liquid #mobTiersWrap / #tiers)
        const mob = document.querySelector('[data-quick-add-mobile] [data-mobile-tier-container]');
        const pc = document.querySelector('[data-quick-add-pc] [data-pc-tier-container]');
        if (mob) { mob.style.display = 'none'; mob.innerHTML = ''; }
        if (pc)  { pc.style.display = 'none';  pc.innerHTML = ''; }
      }
    }

    updateSubtotal();

  } catch (error) {
    console.error('Failed to load product data:', error);
  }
}

// =====================
// Populate Header (tags, title, MOQ)
// =====================

function populateHeader(title, moq, tags, optionNames) {
  // Update SKU table header labels with real option names
  const mobileSkuLabel = document.querySelector('[data-mobile-sku-label]');
  if (mobileSkuLabel && optionNames) mobileSkuLabel.textContent = optionNames;
  const pcSkuLabel = document.querySelector('[data-pc-sku-label]');
  if (pcSkuLabel && optionNames) pcSkuLabel.textContent = optionNames;

  // Mobile
  const mobileMoq = document.querySelector('[data-mobile-moq]');
  if (mobileMoq) mobileMoq.textContent = moq;

  const mobileFreeTag = document.querySelector('[data-mobile-tag-free-shipping]');
  if (mobileFreeTag) {
    if (tags.includes('Free Shipping')) {
      mobileFreeTag.classList.remove('hidden');
      mobileFreeTag.classList.add('flex');
    } else {
      mobileFreeTag.classList.add('hidden');
      mobileFreeTag.classList.remove('flex');
    }
  }

  // Discount tag is now managed by updateHeaderDiscountTag() — no longer set here

  // PC
  const pcTitle = document.querySelector('[data-pc-title]');
  if (pcTitle) pcTitle.textContent = title;

  const pcMoq = document.querySelector('[data-pc-moq]');
  if (pcMoq) pcMoq.textContent = moq;

  const pcFreeTag = document.querySelector('[data-pc-tag-free-shipping]');
  if (pcFreeTag) {
    if (tags.includes('Free Shipping')) {
      pcFreeTag.classList.remove('hidden');
      pcFreeTag.classList.add('flex');
    } else {
      pcFreeTag.classList.add('hidden');
      pcFreeTag.classList.remove('flex');
    }
  }

  // Discount tag is now managed by updateHeaderDiscountTag() — no longer set here
}

// =====================
// Populate Images — Swiper-based
// =====================

async function populateImages(images) {
  currentProductImages = images;
  currentImageIndex = 0;

  await loadSwiper();

  // --- Mobile Main Image ---
  const mobileMainImg = document.querySelector('[data-mobile-main-img]');
  if (mobileMainImg && images.length > 0) {
    mobileMainImg.style.opacity = '0';
    mobileMainImg.onload = null;
    mobileMainImg.src = TRANSPARENT_HOLDER;
    mobileMainImg.dataset.src = images[0];
    mobileMainImg.onload = function() {
      if (this.dataset.src) {
        this.onload = function() { this.style.opacity = '1'; };
        this.src = this.dataset.src;
        delete this.dataset.src;
      }
    };
    // Click to open image viewer
    mobileMainImg.onclick = function() {
      openDrawerImageViewer(images, currentImageIndex);
    };
  }
  // Bind expand icon to open image viewer
  const expandBtn = document.querySelector('[data-mobile-img-expand]');
  if (expandBtn && images.length > 0) {
    expandBtn.onclick = function(e) {
      e.stopPropagation();
      openDrawerImageViewer(currentProductImages, currentImageIndex);
    };
  }

  // --- PC Main Swiper ---
  const pcSwiperEl = document.querySelector('[data-pc-swiper]');
  if (pcSwiperEl) {
    const wrapper = pcSwiperEl.querySelector('.swiper-wrapper');
    wrapper.innerHTML = images.map((img, i) => `
      <div class="swiper-slide flex items-center justify-center">
        <img src="${TRANSPARENT_HOLDER}" data-src="${img}" onload="${IMG_ONLOAD_STATE}" onerror="${IMG_ONERROR_FALLBACK}" class="h-full w-full object-contain" style="${IMG_FADE_STYLE}" />
      </div>
    `).join('');

    // --- PC Thumb Swiper ---
    const pcThumbEl = document.querySelector('[data-pc-thumb-swiper]');
    if (pcThumbEl) {
      const thumbWrapper = pcThumbEl.querySelector('.swiper-wrapper');
      thumbWrapper.innerHTML = images.map((img, i) => `
        <div class="swiper-slide" style="width:72px;margin-right:8px;">
          <div class="h-[72px] w-[72px] cursor-pointer overflow-hidden rounded-[8px] border-2 border-gray-100 opacity-60 hover:opacity-100 transition-opacity">
            <div class="h-full w-full overflow-hidden bg-white">
              <img src="${TRANSPARENT_HOLDER}" data-src="${img}" onload="${IMG_ONLOAD_STATE}" onerror="${IMG_ONERROR_FALLBACK}" class="h-full w-full object-contain" style="${IMG_FADE_STYLE}" />
            </div>
          </div>
        </div>
      `).join('');
    }

    // Destroy existing Swipers
    if (pcThumbSwiper) {
      pcThumbSwiper.destroy(true, true);
      pcThumbSwiper = null;
    }
    if (pcDrawerSwiper) {
      pcDrawerSwiper.destroy(true, true);
      pcDrawerSwiper = null;
    }

    if (images.length > 0) {
      // Init thumb swiper first
      if (pcThumbEl) {
        pcThumbSwiper = new Swiper(pcThumbEl, {
          slidesPerView: 'auto',
          spaceBetween: 8,
          freeMode: true,
          watchSlidesProgress: true
        });
      }

      // Init main swiper with thumbs
      pcDrawerSwiper = new Swiper(pcSwiperEl, {
        slidesPerView: 1,
        thumbs: pcThumbSwiper ? { swiper: pcThumbSwiper } : undefined,
        on: {
          slideChange: function () {
            currentImageIndex = pcDrawerSwiper.activeIndex;
            updatePcThumbActive();
          }
        }
      });

      // Set initial thumb active state
      updatePcThumbActive();
    }
  }
}

function updateMobileImgCounter() {
  const counter = document.querySelector('[data-mobile-img-counter]');
  if (!counter) return;
  const total = currentProductImages.length || 1;
  const current = currentImageIndex + 1;
  counter.textContent = `${current}/${total}`;
}

function updatePcThumbActive() {
  if (!pcThumbSwiper) return;
  const slides = pcThumbSwiper.slides;
  const activeIdx = pcDrawerSwiper ? pcDrawerSwiper.activeIndex : 0;
  slides.forEach((slide, i) => {
    const inner = slide.querySelector('div');
    if (!inner) return;
    if (i === activeIdx) {
      inner.classList.remove('border-gray-100', 'opacity-60');
      inner.classList.add('border-black', 'opacity-100');
    } else {
      inner.classList.remove('border-black', 'opacity-100');
      inner.classList.add('border-gray-100', 'opacity-60');
    }
  });
}

// =====================
// Render SKU Data (both mobile + PC)
// =====================

function renderSkuData(skuData) {
  renderMobileSkuList(skuData);
  renderPcSkuList(skuData);
}

// --- Mobile SKU List ---

function renderMobileSkuList(skuData) {
  const container = document.querySelector('[data-mobile-sku-list]');
  if (!container) return;

  if (skuData.length === 0) {
    container.innerHTML = '<div class="px-3 py-4 text-sm text-gray-400">No variants available</div>';
    return;
  }

  container.innerHTML = skuData.map((sku, index) => {
    var displayQty = sku.qty > 0 ? sku.qty : 1;
    // v1.1: PricingSourceResolver — Full or Lite path, same Presentation Facts shape
    var facts = resolvePricingFacts(sku.id, displayQty);
    var price              = facts ? facts.resolvedPrice.toFixed(2) : (sku.priceBase || 0).toFixed(2);
    var discountPercent    = facts ? facts.marketingDiscountPercent : 0;
    var hasDiscount        = facts ? facts.hasMarketingDiscount : false;
    var strikethroughPrice = hasDiscount ? facts.compareAtDollars.toFixed(2) : '';
    var specLabel = sku.options && sku.options.length
      ? sku.options.map(o => `<span class="text-[11px] text-[#696969]">${o.name}: <span class="text-[#393939]">${o.value}</span></span>`).join('<br>')
      : `<span class="text-xs font-semibold text-[#393939]">${sku.name}</span>`;

    return `
    <div data-sku-item data-variant-id="${sku.id}" data-mobile-sku-index="${index}"
         class="grid grid-cols-[52px_minmax(0,1fr)_88px_120px] items-center px-3 py-2 border-b border-[#F5F5F5] cursor-pointer${index === 0 ? ' bg-[#F9FAFB]' : ''}">
      <div class="relative h-10 w-10 shrink-0" data-sku-img>
        <div class="h-10 w-10 overflow-hidden rounded border border-black">
          ${sku.image ? `<img src="${TRANSPARENT_HOLDER}" data-src="${sku.image}" onload="${IMG_ONLOAD_STATE}" onerror="${IMG_ONERROR_FALLBACK}" class="h-full w-full object-contain" style="${IMG_FADE_STYLE}" />` : `<div class="h-full w-full bg-gray-100 flex items-center justify-center text-[10px] text-gray-400">No Image</div>`}
        </div>
        <span class="sku-badge absolute -top-1 -left-1 flex h-5 min-w-[28px] items-center justify-center rounded bg-[#DC2626] px-1 text-[10px] font-bold text-white${discountPercent === 0 ? ' hidden' : ''}">-${discountPercent}%</span>
      </div>
      <div class="min-w-0 flex flex-col justify-center pl-2">
        ${specLabel}
      </div>
      <div class="text-center">
        <div class="text-sm font-bold text-[#1a1a2e]">$${price}</div>
        <div class="text-[10px] text-[#999999] line-through${hasDiscount ? '' : ' hidden'}">$${strikethroughPrice}</div>
      </div>
      <div class="flex justify-center">
        <div class="flex items-center gap-2 rounded-full border border-[#F0F0F0] bg-white">
          <button data-qty-minus data-variant-id="${sku.id}" ${sku.qty === 0 ? 'disabled' : ''}
                  class="h-[28px] w-[28px] flex items-center justify-center rounded-full ${sku.qty === 0 ? 'text-[#CCCCCC]' : 'text-[#393939]'}">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" d="M5 12h14" />
            </svg>
          </button>
          <input type="text" inputmode="numeric" data-qty-input data-variant-id="${sku.id}" value="${sku.qty}"
                 class="w-10 bg-transparent text-center text-sm font-medium text-[#393939] outline-none" />
          <button data-qty-plus data-variant-id="${sku.id}"
                  class="h-[28px] w-[28px] flex items-center justify-center rounded-full text-[#393939]">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      </div>
    </div>`;
  }).join('');
}

// --- PC SKU List ---

function renderPcSkuList(skuData) {
  const container = document.querySelector('[data-pc-sku-list]');
  if (!container) return;

  if (skuData.length === 0) {
    container.innerHTML = '<div class="px-3 py-4 text-sm text-gray-400">No variants available</div>';
    return;
  }

  container.innerHTML = skuData.map((sku, index) => {
    var displayQty = sku.qty > 0 ? sku.qty : 1;
    // v1.1: PricingSourceResolver — Full or Lite path, same Presentation Facts shape
    var facts = resolvePricingFacts(sku.id, displayQty);
    var price              = facts ? facts.resolvedPrice.toFixed(2) : (sku.priceBase || 0).toFixed(2);
    var discountPercent    = facts ? facts.marketingDiscountPercent : 0;
    var hasDiscount        = facts ? facts.hasMarketingDiscount : false;
    var strikethroughPrice = hasDiscount ? facts.compareAtDollars.toFixed(2) : '';
    var specLabel = sku.options && sku.options.length
      ? sku.options.map(o => `<span class="text-[11px] text-[#696969]">${o.name}: <span class="text-[#393939]">${o.value}</span></span>`).join('<br>')
      : `<span class="text-xs font-semibold text-[#393939]">${sku.name}</span>`;

    return `
    <div data-sku-item data-variant-id="${sku.id}" data-pc-sku-index="${index}"
         class="flex items-center py-3 border-b border-[#F3F4F6] cursor-pointer pl-1${index === 0 ? ' bg-[#F9FAFB]' : ''}">
      <div class="w-12 shrink-0">
        <div class="relative h-10 w-10">
          <div class="h-10 w-10 overflow-hidden rounded-[4px] border border-gray-100 bg-white">
            ${sku.image ? `<img src="${TRANSPARENT_HOLDER}" data-src="${sku.image}" onload="${IMG_ONLOAD_STATE}" onerror="${IMG_ONERROR_FALLBACK}" class="h-full w-full object-contain" style="${IMG_FADE_STYLE}" />` : `<div class="h-full w-full bg-gray-100 flex items-center justify-center text-[10px] text-gray-400">No Image</div>`}
          </div>
          <span class="sku-badge absolute -top-1.5 -left-1.5 rounded bg-red-50 px-1 py-0.5 text-[10px] font-bold text-red-600 shadow-sm${discountPercent === 0 ? ' hidden' : ''}">-${discountPercent}%</span>
        </div>
      </div>
      <div class="flex-1 min-w-0 flex flex-col justify-center pl-2">
        ${specLabel}
      </div>
      <div class="w-[68px] shrink-0 flex flex-col items-center justify-center">
        <div class="text-sm font-bold text-[#333333]">$${price}</div>
        <div class="text-[10px] text-[#999999] line-through${hasDiscount ? '' : ' hidden'}">$${strikethroughPrice}</div>
      </div>
      <div class="w-[108px] shrink-0 flex items-center justify-center">
        <div class="flex items-center gap-2 rounded-full border border-[#F0F0F0] bg-white w-fit">
          <button data-qty-minus data-variant-id="${sku.id}" ${sku.qty === 0 ? 'disabled' : ''}
                  class="flex h-[28px] w-[28px] items-center justify-center rounded-full ${sku.qty === 0 ? 'text-[#CCCCCC]' : 'text-[#393939]'}">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14"></path>
            </svg>
          </button>
          <input type="text" inputmode="numeric" data-qty-input data-variant-id="${sku.id}" value="${sku.qty}"
                 class="h-[28px] max-w-10 bg-white text-center text-sm font-medium text-[#393939] outline-none focus:outline-none" />
          <button data-qty-plus data-variant-id="${sku.id}"
                  class="flex h-[28px] w-[28px] items-center justify-center rounded-full text-[#393939]">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14"></path>
              <path d="M12 5v14"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>`;
  }).join('');
}

// =====================
// Quantity Buttons
// =====================

function setupQuantityButtons() {
  // Plus buttons (all — mobile and PC share data-qty-plus)
  document.querySelectorAll('[data-qty-plus]').forEach(btn => {
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      const variantId = parseInt(this.getAttribute('data-variant-id'), 10);
      const sku = currentSkuData.find(s => s.id === variantId);
      if (!sku) return;

      sku.qty += 1;

      // Update both mobile & PC inputs for this variant
      document.querySelectorAll(`[data-qty-input][data-variant-id="${variantId}"]`).forEach(input => {
        input.value = sku.qty;
      });

      // Enable minus buttons
      document.querySelectorAll(`[data-qty-minus][data-variant-id="${variantId}"]`).forEach(minusBtn => {
        minusBtn.disabled = false;
        minusBtn.classList.remove('text-[#CCCCCC]');
        minusBtn.classList.add('text-[#393939]');
      });

      // Highlight active SKU
      currentActiveSkuId = variantId;
      highlightActiveSku(variantId);

      // Update tier & subtotal
      renderTierPricing(variantId);
      const activeSku = currentSkuData.find(s => s.id === currentActiveSkuId);
      if (activeSku) updateTierHighlight(activeSku.qty > 0 ? activeSku.qty : 1);
      updateHeaderDiscountTag(activeSku);
      refreshSkuRow(variantId);
      updateSubtotal();
    });
  });

  // Minus buttons
  document.querySelectorAll('[data-qty-minus]').forEach(btn => {
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      const variantId = parseInt(this.getAttribute('data-variant-id'), 10);
      const sku = currentSkuData.find(s => s.id === variantId);
      if (!sku || sku.qty <= 0) return;

      sku.qty -= 1;

      // Update both inputs
      document.querySelectorAll(`[data-qty-input][data-variant-id="${variantId}"]`).forEach(input => {
        input.value = sku.qty;
      });

      // Disable minus if qty is 0
      if (sku.qty === 0) {
        document.querySelectorAll(`[data-qty-minus][data-variant-id="${variantId}"]`).forEach(minusBtn => {
          minusBtn.disabled = true;
          minusBtn.classList.add('text-[#CCCCCC]');
          minusBtn.classList.remove('text-[#393939]');
        });
      }

      // Update tier & subtotal
      renderTierPricing(variantId);
      const activeSku = currentSkuData.find(s => s.id === currentActiveSkuId);
      if (activeSku) updateTierHighlight(activeSku.qty > 0 ? activeSku.qty : 1);
      updateHeaderDiscountTag(activeSku);
      refreshSkuRow(variantId);
      updateSubtotal();
    });
  });

  // Input handlers: change (final value) + input (real-time, aligned with PC product page)
  document.querySelectorAll('[data-qty-input]').forEach(input => {
    // oninput — real-time response (product page uses oninput for instant feedback)
    input.addEventListener('input', function () {
      var variantId = parseInt(this.getAttribute('data-variant-id'), 10);
      var raw = this.value.trim();
      var v = parseInt(raw, 10);
      if (isNaN(v) || v < 0) v = 0;
      var norm = String(v);
      if (this.value !== norm) this.value = norm;

      var sku = currentSkuData.find(function(s) { return s.id === variantId; });
      if (!sku || sku.qty === v) return;

      sku.qty = v;

      // Sync other input for this variant
      document.querySelectorAll('[data-qty-input][data-variant-id="' + variantId + '"]').forEach(function(inp) {
        if (inp !== input) inp.value = v;
      });

      // Minus button state
      document.querySelectorAll('[data-qty-minus][data-variant-id="' + variantId + '"]').forEach(function(minusBtn) {
        minusBtn.disabled = v === 0;
        if (v === 0) {
          minusBtn.classList.add('text-[#CCCCCC]');
          minusBtn.classList.remove('text-[#393939]');
        } else {
          minusBtn.classList.remove('text-[#CCCCCC]');
          minusBtn.classList.add('text-[#393939]');
        }
      });

      if (v > 0) {
        currentActiveSkuId = variantId;
        highlightActiveSku(variantId);
      }

      renderTierPricing(variantId);
      var activeSku = currentSkuData.find(function(s) { return s.id === currentActiveSkuId; });
      if (activeSku) updateTierHighlight(activeSku.qty > 0 ? activeSku.qty : 1);
      updateHeaderDiscountTag(activeSku);
      refreshSkuRow(variantId);
      updateSubtotal();
    });

    // onfocus — auto-select when value is "0" (aligned with PC product page)
    input.addEventListener('focus', function () {
      if (this.value === '0') this.select();
    });

    // change — final value commit (kept for blur/Enter)
    input.addEventListener('change', function () {
      var variantId = parseInt(this.getAttribute('data-variant-id'), 10);
      var newQty = parseInt(this.value, 10) || 0;
      if (newQty < 0) newQty = 0;

      var sku = currentSkuData.find(function(s) { return s.id === variantId; });
      if (!sku) return;

      sku.qty = newQty;

      // Sync both inputs
      document.querySelectorAll('[data-qty-input][data-variant-id="' + variantId + '"]').forEach(function(inp) {
        inp.value = newQty;
      });

      // Minus button state
      document.querySelectorAll('[data-qty-minus][data-variant-id="' + variantId + '"]').forEach(function(minusBtn) {
        minusBtn.disabled = newQty === 0;
        if (newQty === 0) {
          minusBtn.classList.add('text-[#CCCCCC]');
          minusBtn.classList.remove('text-[#393939]');
        } else {
          minusBtn.classList.remove('text-[#CCCCCC]');
          minusBtn.classList.add('text-[#393939]');
        }
      });

      if (newQty > 0) {
        currentActiveSkuId = variantId;
        highlightActiveSku(variantId);
      }

      renderTierPricing(variantId);
      var activeSku = currentSkuData.find(function(s) { return s.id === currentActiveSkuId; });
      if (activeSku) updateTierHighlight(activeSku.qty > 0 ? activeSku.qty : 1);
      updateHeaderDiscountTag(activeSku);
      refreshSkuRow(variantId);
      updateSubtotal();
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') this.blur();
    });
  });

  // SKU item click → set active + switch PC image
  document.querySelectorAll('[data-sku-item]').forEach(item => {
    item.addEventListener('click', function (e) {
      // Open image viewer if clicking on SKU thumbnail
      if (e.target.closest('[data-sku-img]')) {
        e.stopPropagation();
        const variantId = parseInt(this.getAttribute('data-variant-id'), 10);
        const skuIdx = currentSkuData.findIndex(s => s.id === variantId);
        const skuImagesList = currentSkuData.map(function(s) { return s.image; }).filter(Boolean);
        if (skuImagesList.length > 0) {
          openDrawerImageViewer(skuImagesList, skuIdx >= 0 ? skuIdx : 0);
        }
        return;
      }

      // Don't activate if clicking qty controls
      if (e.target.closest('[data-qty-plus]') || e.target.closest('[data-qty-minus]') || e.target.closest('[data-qty-input]')) return;

      const variantId = parseInt(this.getAttribute('data-variant-id'), 10);
      currentActiveSkuId = variantId;
      highlightActiveSku(variantId);

      const sku = currentSkuData.find(s => s.id === variantId);
      if (sku?.image) {
        switchToImageByVariant(variantId);
      }

      renderTierPricing(variantId);
      const activeSku = currentSkuData.find(s => s.id === currentActiveSkuId);
      if (activeSku) updateTierHighlight(activeSku.qty > 0 ? activeSku.qty : 1);
    });
  });
}

// =====================
// Highlight Active SKU Row
// =====================

function highlightActiveSku(variantId) {
  document.querySelectorAll('[data-sku-item]').forEach(item => {
    const id = parseInt(item.getAttribute('data-variant-id'), 10);
    if (id === variantId) {
      item.classList.add('bg-[#F9FAFB]');
    } else {
      item.classList.remove('bg-[#F9FAFB]');
    }
  });

  // Update mobile main image to active SKU image
  const activeSku = currentSkuData.find(s => s.id === variantId);
  if (activeSku && activeSku.image) {
    const mobileMainImg = document.querySelector('[data-mobile-main-img]');
    if (mobileMainImg) mobileMainImg.src = activeSku.image;
  }

  // Update header discount tag to match active SKU
  if (activeSku) {
    updateHeaderDiscountTag(activeSku);
  }
}

// v1.1: PricingSourceResolver — Full when available, Lite when not
function updateHeaderDiscountTag(sku) {
  var disc = 0;
  if (sku && sku.id) {
    var qty = sku.qty > 0 ? sku.qty : 1;
    var facts = resolvePricingFacts(sku.id, qty);
    if (facts) disc = facts.marketingDiscountPercent;
  }

  // QuickAdd-specific DOM rendering (F2.6.4 TODO: unify with JWProductDisplay)
  var mobileDiscountTag = document.querySelector('[data-mobile-tag-discount]');
  if (mobileDiscountTag) {
    if (disc > 0) {
      mobileDiscountTag.textContent = disc + '% OFF';
      mobileDiscountTag.classList.remove('hidden');
      mobileDiscountTag.classList.add('flex');
    } else {
      mobileDiscountTag.classList.add('hidden');
      mobileDiscountTag.classList.remove('flex');
    }
  }

  var pcDiscountTag = document.querySelector('[data-pc-tag-discount]');
  if (pcDiscountTag) {
    if (disc > 0) {
      pcDiscountTag.textContent = disc + '% OFF';
      pcDiscountTag.classList.remove('hidden');
      pcDiscountTag.classList.add('inline');
    } else {
      pcDiscountTag.classList.add('hidden');
      pcDiscountTag.classList.remove('inline');
    }
  }
}

// =====================
// v1.1: Tier price resolver — Full Resolver preferred, Lite path when unavailable
function getTierPriceByQty(skuId, basePriceCents, qty) {
  var sku = currentSkuData.find(function(s) { return s.id === skuId; });
  if (!sku || !sku.priceMap) return basePriceCents;
  var price = JWLitePricingResolver._selectTier(sku.priceMap.tiers, basePriceCents / 100, qty);
  return Math.round(price * 100);  // Return cents
}

// =====================
// Render Tier Pricing — F2.6.3.3: data from JWProductPricing
// =====================

var _cachedTierSkuId = null;
var _cachedTiersJson = '';

function renderTierPricing(skuId) {
  var sku = currentSkuData.find(function(s) { return s.id === skuId; });
  if (!sku) return;

  var displayQty = sku.qty > 0 ? sku.qty : 1;
  var facts = resolvePricingFacts(skuId, displayQty);
  if (!facts) return;

  var tiers = facts.tiers;
  var priceBase = facts.baseTierPrice;
  var compareAtPrice = facts.compareAtDollars || priceBase;

  // Skip full DOM rebuild when SKU & tiers unchanged
  if (skuId === _cachedTierSkuId) {
    var tiersJson = JSON.stringify(tiers);
    if (tiersJson === _cachedTiersJson) {
      var activeSku = currentSkuData.find(function(s) { return s.id === currentActiveSkuId; });
      if (activeSku) updateTierHighlight(activeSku.qty > 0 ? activeSku.qty : 1);
      return;
    }
  }

  _cachedTierSkuId = skuId;
  _cachedTiersJson = JSON.stringify(tiers);

  renderMobileTierPricing(tiers, priceBase, compareAtPrice);
  renderPcTierPricing(tiers, priceBase, compareAtPrice);
}

function renderMobileTierPricing(tiers, priceBase, compareAtPrice) {
  // SCOPE: Drawer internal only — must not target product.liquid #mobTiersWrap
  const container = document.querySelector('[data-quick-add-mobile] [data-mobile-tier-container]');
  if (!container) return;

  if (!tiers || tiers.length === 0) {
    // Animate collapse → then clear
    container.classList.add('tier-hidden');
    setTimeout(function(){ if(container.classList.contains('tier-hidden')) container.innerHTML = ''; }, 400);
    return;
  }

  // Remove hidden state before render (for first show)
  var wasHidden = container.classList.contains('tier-hidden');
  // Price Contract v1.1: show ALL tiers including min=1 base tier
  var visibleTiers = tiers;
  if (visibleTiers.length === 0) {
    container.innerHTML = '<div class="text-xs text-gray-400 px-2">No bulk tiers</div>';
    return;
  }
  container.innerHTML = visibleTiers.map((tier, index) => {
    const price = ((tier.price != null && tier.price > 0) ? tier.price : priceBase).toFixed(2);
    const range = formatTierRange(visibleTiers, index);
    const tierPrice = Number(tier.price) || priceBase;
    // Strike shows Marketing Engine MSRP whenever it's above the tier price
    const strikePrice = (compareAtPrice && compareAtPrice > 0) ? compareAtPrice : priceBase;
    const showCompare = tierPrice < strikePrice;
    return `
    <div data-tier-item data-tier-index="${index}"
         class="shrink-0 flex flex-col items-center rounded bg-white border border-transparent p-2"
         style="opacity:0;transform:translateY(6px)">
      <span class="text-[10px] text-[#696969]">${range}</span>
      <span class="text-xs font-bold text-[#393939]">$${price}</span>
      ${showCompare ? `<span class="text-[10px] text-[#999999] line-through">$${strikePrice.toFixed(2)}</span>` : ''}
    </div>`;
  }).join('');

  // Animate: remove tier-hidden → cards fade in
  if (wasHidden) {
    container.style.display = '';
    container.offsetHeight;
    container.classList.remove('tier-hidden');
  }
  // Staggered card reveal
  container.querySelectorAll('[data-tier-item]').forEach(function(card, i) {
    setTimeout(function(){ card.style.opacity=''; card.style.transform=''; }, 30 + i * 50);
  });

  // Highlight active
  const activeSku = currentSkuData.find(s => s.id === currentActiveSkuId);
  if (activeSku) updateTierHighlight(activeSku.qty > 0 ? activeSku.qty : 1);
  bindTierCardClicks();
}

function renderPcTierPricing(tiers, priceBase, compareAtPrice) {
  // SCOPE: Drawer internal only — must not target product.liquid #tiers
  const container = document.querySelector('[data-quick-add-pc] [data-pc-tier-container]');
  if (!container) return;

  if (!tiers || tiers.length === 0) {
    container.classList.add('tier-hidden');
    setTimeout(function(){ if(container.classList.contains('tier-hidden')) container.innerHTML = ''; }, 400);
    return;
  }

  var wasHidden = container.classList.contains('tier-hidden');
  // Price Contract v1.1: show ALL tiers including min=1 base tier
  var visibleTiers = tiers;
  if (visibleTiers.length === 0) {
    container.innerHTML = '<div class="text-xs text-gray-400 px-2">No bulk tiers</div>';
    return;
  }
  container.innerHTML = visibleTiers.map((tier, index) => {
    const price = ((tier.price != null && tier.price > 0) ? tier.price : priceBase).toFixed(2);
    const range = formatTierRange(visibleTiers, index);
    const tierPrice = Number(tier.price) || priceBase;
    // Strike shows Marketing Engine MSRP whenever it's above the tier price
    const strikePrice = (compareAtPrice && compareAtPrice > 0) ? compareAtPrice : priceBase;
    const showCompare = tierPrice < strikePrice;
    return `
    <div data-tier-item data-tier-index="${index}"
         class="flex flex-1 shrink-0 cursor-pointer flex-col gap-1 rounded-[4px] bg-[#F9FAFB] p-2"
         style="opacity:0;transform:translateY(6px)">
      <div class="whitespace-nowrap text-xs text-[#696969]">${range}</div>
      <div class="text-[20px] font-bold leading-[24px] text-[#393939]">$${price}</div>
      ${showCompare ? `<div class="text-[13px] leading-[16px] text-[#999999] line-through">$${strikePrice.toFixed(2)}</div>` : ''}
    </div>`;
  }).join('');

  if (wasHidden) {
    container.style.display = '';
    container.offsetHeight;
    container.classList.remove('tier-hidden');
  }
  container.querySelectorAll('[data-tier-item]').forEach(function(card, i) {
    setTimeout(function(){ card.style.opacity=''; card.style.transform=''; }, 30 + i * 50);
  });

  const activeSku = currentSkuData.find(s => s.id === currentActiveSkuId);
  if (activeSku) updateTierHighlight(activeSku.qty > 0 ? activeSku.qty : 1);
  bindTierCardClicks();
}

function formatTierRange(tiers, index) {
  const tier = tiers[index];
  // Last tier: open-ended
  if (index === tiers.length - 1) {
    return `≥${tier.min} pcs`;
  }
  // Middle tiers: compute max from next tier's min
  const nextMin = tiers[index + 1].min;
  const max = nextMin - 1;
  if (tier.min === max) return `${tier.min} pcs`;
  return `${tier.min}-${max} pcs`;
}

// =====================
// Tier Highlight
// =====================

function updateTierHighlight(qty) {
  var facts = resolvePricingFacts(currentActiveSkuId, qty);
  var activeIndex = facts ? facts.activeTierIndex : -1;
  if (activeIndex < 0) return;

  // Mobile tiers (Drawer-scoped)
  document.querySelectorAll('[data-quick-add-mobile] [data-mobile-tier-container] [data-tier-item]').forEach((card, i) => {
    if (i === activeIndex) {
      card.classList.remove('border-transparent', 'bg-white');
      card.classList.add('border-black', 'bg-[#EBF5FF]');
    } else {
      card.classList.remove('border-black', 'bg-[#EBF5FF]');
      card.classList.add('border-transparent', 'bg-white');
    }
  });

  // PC tiers (Drawer-scoped)
  document.querySelectorAll('[data-quick-add-pc] [data-pc-tier-container] [data-tier-item]').forEach((card, i) => {
    if (i === activeIndex) {
      card.classList.add('bg-[#F9FAFB]');
      card.classList.remove('border', 'border-transparent');
      card.classList.add('border', 'border-black');
    } else {
      card.classList.remove('border-black');
      card.classList.add('border-transparent');
    }
  });

  // Scroll mobile tier into view
  scrollToActiveTier();
}

// v1.1: activeTierIndex now via resolvePricingFacts().activeTierIndex

function scrollToActiveTier() {
  // SCOPE: Drawer internal only
  const container = document.querySelector('[data-quick-add-mobile] [data-mobile-tier-container]');
  const activeCard = container?.querySelector('.border-black');
  if (!container || !activeCard) return;

  const containerRect = container.getBoundingClientRect();
  const activeRect = activeCard.getBoundingClientRect();
  const isVisible = activeRect.left >= containerRect.left && activeRect.right <= containerRect.right;
  if (isVisible) return;

  const scrollLeft = container.scrollLeft;
  let targetLeft = scrollLeft + (activeRect.left - containerRect.left) - (containerRect.width / 2) + (activeRect.width / 2);
  const maxScroll = container.scrollWidth - container.clientWidth;
  targetLeft = Math.max(0, Math.min(targetLeft, maxScroll));
  container.scrollTo({ left: targetLeft, behavior: 'smooth' });
}

// =====================
// Tier Card Click → Set Qty (aligned with PC product page)
// =====================

function bindTierCardClicks() {
  document.querySelectorAll('[data-tier-item]').forEach(function(card) {
    card.onclick = function() {
      var capturedSkuId = currentActiveSkuId;
      if (!capturedSkuId) return;
      var index = parseInt(card.getAttribute('data-tier-index'), 10);
      var sku = currentSkuData.find(function(s) { return s.id === capturedSkuId; });
      if (!sku || !sku.priceMap || !sku.priceMap.tiers || !sku.priceMap.tiers[index]) return;

      var tierMin = sku.priceMap.tiers[index].min || 1;
      sku.qty = tierMin;

      // Sync all inputs for this variant
      document.querySelectorAll('[data-qty-input][data-variant-id="' + capturedSkuId + '"]').forEach(function(inp) {
        inp.value = tierMin;
      });

      // Enable minus buttons
      document.querySelectorAll('[data-qty-minus][data-variant-id="' + capturedSkuId + '"]').forEach(function(btn) {
        btn.disabled = false;
        btn.classList.remove('text-[#CCCCCC]');
        btn.classList.add('text-[#393939]');
      });

      refreshSkuRow(capturedSkuId);
      updateTierHighlight(tierMin);
      updateHeaderDiscountTag(sku);
      updateSubtotal();
    };
  });
}

// =====================
// Update Subtotal (both mobile + PC)
// =====================

function updateSubtotal() {
  let totalAmount = 0;
  let totalPieces = 0;

  currentSkuData.forEach(sku => {
    if (sku.qty > 0) {
      var facts = resolvePricingFacts(sku.id, sku.qty);
      var price = facts ? facts.resolvedPrice : (sku.priceBase || 0);
      totalAmount += price * sku.qty;
      totalPieces += sku.qty;
    }
  });

  // Mobile
  const mobileTotalPieces = document.querySelector('[data-mobile-total-pieces]');
  const mobileSubtotal = document.querySelector('[data-mobile-subtotal]');
  const mobileAddBtn = document.querySelector('[data-mobile-add-btn]');

  if (mobileTotalPieces) mobileTotalPieces.textContent = totalPieces;
  if (mobileSubtotal) mobileSubtotal.textContent = `$${totalAmount.toFixed(2)}`;
  if (mobileAddBtn) {
    mobileAddBtn.disabled = totalPieces === 0;
    if (totalPieces === 0) {
      mobileAddBtn.classList.add('bg-gray-300', 'text-gray-500');
      mobileAddBtn.classList.remove('bg-[#1a1a2e]', 'text-white');
    } else {
      mobileAddBtn.classList.remove('bg-gray-300', 'text-gray-500');
      mobileAddBtn.classList.add('bg-[#1a1a2e]', 'text-white');
    }
  }

  // PC
  const pcTotalPieces = document.querySelector('[data-pc-total-pieces]');
  const pcSubtotal = document.querySelector('[data-pc-subtotal]');
  const pcAddBtn = document.querySelector('[data-pc-add-btn]');

  if (pcTotalPieces) pcTotalPieces.textContent = totalPieces;
  if (pcSubtotal) pcSubtotal.textContent = `$${totalAmount.toFixed(2)}`;
  if (pcAddBtn) {
    pcAddBtn.disabled = totalPieces === 0;
    if (totalPieces === 0) {
      pcAddBtn.classList.add('bg-gray-300', 'text-gray-500');
      pcAddBtn.classList.remove('bg-[#1a1a2e]', 'text-white');
    } else {
      pcAddBtn.classList.remove('bg-gray-300', 'text-gray-500');
      pcAddBtn.classList.add('bg-[#1a1a2e]', 'text-white');
    }
  }
}

// =====================
// PC Image Gallery — Swiper-based
// =====================

function switchToImageByVariant(variantId) {
  const sku = currentSkuData.find(s => s.id === variantId);
  if (!sku?.image) return;

  // Try to find the image in product images that matches the variant image
  const idx = currentProductImages.findIndex(img => img === sku.image);
  if (idx >= 0 && pcDrawerSwiper) {
    pcDrawerSwiper.slideTo(idx);
  } else {
    // If not found in product images, switch to SKU images mode
    const skuImages = currentSkuData.map(s => s.image).filter(Boolean);
    currentProductImages = skuImages;
    const variantIndex = currentSkuData.findIndex(s => s.id === variantId);
    currentImageIndex = variantIndex >= 0 ? variantIndex : 0;

    // Re-render Swipers with SKU images
    populateImagesSync(skuImages, currentImageIndex);
  }
}

function populateImagesSync(images, activeIndex) {
  // Mobile Main Image
  const mobileMainImg = document.querySelector('[data-mobile-main-img]');
  if (mobileMainImg && images.length > 0) {
    mobileMainImg.style.opacity = '0';
    mobileMainImg.onload = null;
    mobileMainImg.src = TRANSPARENT_HOLDER;
    mobileMainImg.dataset.src = images[activeIndex] || images[0];
    mobileMainImg.onload = function() {
      if (this.dataset.src) {
        this.onload = function() { this.style.opacity = '1'; };
        this.src = this.dataset.src;
        delete this.dataset.src;
      }
    };
  }
  // Re-bind expand icon (images array may have changed via SKU switch)
  const expandBtn = document.querySelector('[data-mobile-img-expand]');
  if (expandBtn && currentProductImages.length > 0) {
    expandBtn.onclick = function(e) {
      e.stopPropagation();
      openDrawerImageViewer(currentProductImages, currentImageIndex);
    };
  }

  // PC Main Swiper
  const pcSwiperEl = document.querySelector('[data-pc-swiper]');
  if (pcSwiperEl && pcDrawerSwiper) {
    const wrapper = pcSwiperEl.querySelector('.swiper-wrapper');
    wrapper.innerHTML = images.map((img, i) => `
      <div class="swiper-slide flex items-center justify-center">
        <img src="${TRANSPARENT_HOLDER}" data-src="${img}" onload="${IMG_ONLOAD_STATE}" onerror="${IMG_ONERROR_FALLBACK}" class="h-full w-full object-contain" style="${IMG_FADE_STYLE}" />
      </div>
    `).join('');

    // PC Thumb Swiper
    const pcThumbEl = document.querySelector('[data-pc-thumb-swiper]');
    if (pcThumbEl && pcThumbSwiper) {
      const thumbWrapper = pcThumbEl.querySelector('.swiper-wrapper');
      thumbWrapper.innerHTML = images.map((img, i) => `
        <div class="swiper-slide" style="width:72px;margin-right:8px;">
          <div class="h-[72px] w-[72px] cursor-pointer overflow-hidden rounded-[8px] border-2 border-gray-100 opacity-60 hover:opacity-100 transition-opacity">
            <div class="h-full w-full overflow-hidden bg-white">
              <img src="${TRANSPARENT_HOLDER}" data-src="${img}" onload="${IMG_ONLOAD_STATE}" onerror="${IMG_ONERROR_FALLBACK}" class="h-full w-full object-contain" style="${IMG_FADE_STYLE}" />
            </div>
          </div>
        </div>
      `).join('');
      pcThumbSwiper.update();
    }

    pcDrawerSwiper.update();
    pcDrawerSwiper.slideTo(activeIndex);
    updatePcThumbActive();
  }
}

// =====================
// Add to Cart — F2.6.3.2: delegate to JWProductCart
// =====================

async function handleAddToCart() {
  // Build payload via JWProductCart (F2.6.3.2)
  var items = currentSkuData
    .filter(function(s) { return s.qty > 0; });

  if (items.length === 0) return;

  var payload = items.map(function(s) {
    return window.JWProductCart && typeof window.JWProductCart.buildPayload === 'function'
      ? window.JWProductCart.buildPayload(s.id, s.qty)
      : { id: s.id, quantity: s.qty }; // F2.6.4 TODO: remove fallback
  });

  var itemCount = payload.reduce(function(a, x) { return a + x.quantity; }, 0);
  var totalAmount = 0;
  // UX-only total (toast) — uses Pricing Resolver
  items.forEach(function(s) {
    var facts = resolvePricingFacts(s.id, s.qty);
    var price = facts ? facts.resolvedPrice : (s.priceBase || 0);
    totalAmount += price * s.qty;
  });

  try {
    // F2.6.4: JWProductCart authority — no legacy fallback
    await window.JWProductCart.add(payload);

    closeQuickAddDrawer();
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));

    var cartData = await fetch('/cart.js').then(function(r) { return r.json(); });
    var cartCountEls = document.querySelectorAll('[data-cart-count]');
    cartCountEls.forEach(function(el) { el.textContent = cartData.item_count; });

    showToast('Added ' + itemCount + ' item(s) — Total: $' + totalAmount.toFixed(2));
  } catch (e) {
    console.error('Add to cart error:', e);
    showToast(e.message || 'Failed to add to cart. Please try again.', 'error');
  }
}

// =====================
// Toast — matches product page style (fixed bottom-center pill)
// =====================

function showToast(message, type) {
  type = type || 'success';
  var el = document.createElement('div');
  el.style.cssText = 'position:fixed;bottom:2rem;left:50%;transform:translateX(-50%) translateY(20px);' +
    'color:#fff;padding:.5rem 1.5rem;border-radius:9999px;font-size:13px;' +
    'opacity:0;transition:all .3s;pointer-events:none;z-index:9999;white-space:nowrap;';
  el.style.background = type === 'error' ? '#DC2626' : '#393939';
  el.textContent = message;
  document.body.appendChild(el);

  // Animate in
  requestAnimationFrame(function() {
    el.style.opacity = '1';
    el.style.transform = 'translateX(-50%) translateY(0)';
  });

  // Animate out + remove
  setTimeout(function() {
    el.style.opacity = '0';
    el.style.transform = 'translateX(-50%) translateY(20px)';
    setTimeout(function() { el.remove(); }, 300);
  }, 2000);
}
